/**
 * News Tab Component
 * 
 * Monitor industry news, Federal Register alerts, and Executive Orders.
 * Extracted from IndustryNewsPage.tsx for use in unified External Data Dashboard.
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Badge, LoadingSpinner, EmptyState } from '@tracekit/core';
import { useWorkspace } from '@tracekit/core';
import {
  Bell,
  Clock,
  FileText,
  Newspaper,
  Scale,
  ChevronLeft,
  ChevronRight,
  Settings,
  ArrowUpDown,
} from 'lucide-react';
import {
  fetchExecutiveOrders,
  fetchFederalRegisterDocuments,
  RSSFeedItem,
} from '../../../services/usGovernmentIntegrations';
import { DataSourceConfig } from '../../../services/dataSourceIntegrations';
import { IndustryNewsArticleCard } from '../../IndustryNewsArticleCard';
import { IndustryNewsArticleModal } from '../../IndustryNewsArticleModal';
import { IndustryNewsSearchBar } from '../../IndustryNewsSearchBar';
import { IndustryNewsPagination } from '../../IndustryNewsPagination';
import { IndustryNewsAlertSettingsPanel } from '../IndustryNewsAlertSettingsPanel';
import { UsFederalRegisterRegionCta } from '../UsFederalRegisterRegionCta';
import { RSS_FEED_REGISTRY, loadSelectedFeedIds } from '../../../services/rssFeedRegistry';
import { useExternalData } from '../../../contexts/ExternalDataContext';
import { useGeographicZone } from '../../../contexts/GeographicZoneContext';
import {
  rssFeedMatchesGeographicZone,
  supportsFederalRegisterTabs,
} from '../../../utils/countryAccess';
import { DEFAULT_NEWS_SIMILARITY_THRESHOLD } from '../../../api/industryNews';
import { useIndustryNewsRanking } from '../../../hooks/useIndustryNewsRanking';
import { useProgressiveRssFeeds } from '../../../hooks/useProgressiveRssFeeds';
import { useIndustryNewsSearch } from '../../../hooks/useIndustryNewsSearch';
import { IndustryNewsSortMode } from '../../../utils/industryNewsRanking';
import { filterIndustryNewsArticles } from '../../../utils/industryNewsSearch';
import {
  INDUSTRY_NEWS_PAGE_SIZE,
  getIndustryNewsTotalPages,
  sliceIndustryNewsPage,
} from '../../../utils/industryNewsConstants';
import { formatIndustryNewsThresholdSummary } from '../../../utils/industryNewsThresholdSummary';

export function NewsTab() {
  const { projectId } = useWorkspace();
  const { 
    selectedGeographicZone,
    selectedKeywords,
    setSelectedKeywords,
  } = useExternalData();
  const { zoneFilterValue: geographicZoneFilter, setSelectedGeographicZone } =
    useGeographicZone();
  const showFederalRegisterTabs = supportsFederalRegisterTabs(selectedGeographicZone);
  const alertSettingsRef = useRef<HTMLDivElement>(null);

  // RSS Feed Selection
  const [selectedFeedIds, setSelectedFeedIds] = useState<string[]>(loadSelectedFeedIds);

  const zoneFilteredFeedIds = useMemo(
    () =>
      selectedFeedIds.filter((feedId) => {
        const feed = RSS_FEED_REGISTRY.find((f) => f.id === feedId);
        if (!feed) return true;
        return rssFeedMatchesGeographicZone(feed.category, selectedGeographicZone);
      }),
    [selectedFeedIds, selectedGeographicZone]
  );
  
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(300000); // 5 minutes
  const [newsPage, setNewsPage] = useState(1);
  const [federalPage, setFederalPage] = useState(1);
  const [selectedArticle, setSelectedArticle] = useState<RSSFeedItem | null>(null);
  const [articleModalOpen, setArticleModalOpen] = useState(false);
  const [alertSettingsOpen, setAlertSettingsOpen] = useState(false);
  const [eoPage, setEoPage] = useState<number>(1);
  const currentYear = new Date().getFullYear();
  const [eoFilters, setEoFilters] = useState({ year: String(currentYear), president: 'all' });
  const [newsSortMode, setNewsSortMode] = useState<IndustryNewsSortMode>('relevance');
  const [similarityThreshold, setSimilarityThreshold] = useState(DEFAULT_NEWS_SIMILARITY_THRESHOLD);
  const { searchQuery, setSearchQuery, clearSearch, hasSearch } = useIndustryNewsSearch();
  
  // Save feed selection to localStorage
  useEffect(() => {
    localStorage.setItem('rfp-selected-feeds', JSON.stringify(selectedFeedIds));
  }, [selectedFeedIds]);
  
  // Base config for data fetching
  const baseConfig: DataSourceConfig = {
    enabled: true,
    sync_frequency: 'hourly',
    filters: {
      keywords: selectedKeywords,
      feedIds: zoneFilteredFeedIds,
      // TODO: pass geographic_zone when Industry News API supports regional scoping
      ...(geographicZoneFilter ? { geographic_zone: geographicZoneFilter } : {}),
    },
  };
  
  // Fetch RSS feeds progressively — articles appear as proxy batches complete
  const {
    articles: rssItems,
    isLoading: loadingRSS,
    isFetching: isFetchingRSS,
    feedLoadProgress,
    refetch: refetchRSS,
  } = useProgressiveRssFeeds({
    config: baseConfig,
    projectId,
    enabled: !!projectId && zoneFilteredFeedIds.length > 0,
    refreshInterval: autoRefresh ? refreshInterval : false,
  });

  useEffect(() => {
    setNewsPage(1);
    setFederalPage(1);
    setEoPage(1);
  }, [selectedGeographicZone]);

  useEffect(() => {
    if (!alertSettingsOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!alertSettingsRef.current?.contains(event.target as Node)) {
        setAlertSettingsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [alertSettingsOpen]);

  const {
    rankedArticles: rankedRssItems,
    isRanking,
    rankingUnavailable,
    aboveThresholdCount,
  } = useIndustryNewsRanking({
    projectId,
    articles: rssItems,
    keywords: selectedKeywords,
    sortMode: newsSortMode,
    similarityThreshold,
    enabled: !!projectId && zoneFilteredFeedIds.length > 0,
  });

  const filteredRssItems = useMemo(
    () => filterIndustryNewsArticles(rankedRssItems, searchQuery),
    [rankedRssItems, searchQuery]
  );

  const newsPageItems = sliceIndustryNewsPage(filteredRssItems, newsPage);

  useEffect(() => {
    setNewsPage(1);
  }, [
    filteredRssItems.length,
    newsSortMode,
    similarityThreshold,
    selectedKeywords.join('|'),
    searchQuery,
  ]);
  
  // Fetch Federal Register alerts (US-only)
  const { data: federalAlerts = [], isLoading: loadingFederal } = useQuery({
    queryKey: ['federal-register-alerts', projectId, selectedKeywords, selectedGeographicZone],
    queryFn: () => fetchFederalRegisterDocuments(baseConfig, projectId || ''),
    enabled: !!projectId && showFederalRegisterTabs && autoRefresh,
    refetchInterval: autoRefresh ? refreshInterval : false,
  });

  const federalTotalPages = getIndustryNewsTotalPages(federalAlerts.length);
  const federalPageItems = sliceIndustryNewsPage(federalAlerts, federalPage);

  useEffect(() => {
    setFederalPage(1);
  }, [federalAlerts.length, selectedKeywords.join('|')]);
  
  // Fetch Executive Orders (US-only)
  const { data: executiveOrders = [], isLoading: loadingEOs } = useQuery({
    queryKey: ['executive-orders', projectId, selectedKeywords, selectedGeographicZone],
    queryFn: () => fetchExecutiveOrders(baseConfig, projectId || ''),
    enabled: !!projectId && showFederalRegisterTabs,
  });

  // Year options for EO filter: 2017 through current year
  const eoYearOptions = useMemo(() => {
    const end = new Date().getFullYear();
    const years: number[] = [];
    for (let y = 2017; y <= end; y++) years.push(y);
    return years.reverse(); // newest first
  }, []);

  // Filter executive orders by selected year (from publication_date) and president
  const filteredExecutiveOrders = useMemo(() => {
    const slug = (s: string) =>
      (s || '').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return executiveOrders.filter((eo) => {
      const year = eo.publication_date
        ? new Date(eo.publication_date).getFullYear()
        : null;
      const yearMatch = year !== null && String(year) === eoFilters.year;
      const presidentMatch =
        eoFilters.president === 'all' || slug(eo.president) === eoFilters.president;
      return yearMatch && presidentMatch;
    });
  }, [executiveOrders, eoFilters.year, eoFilters.president]);
  
  if (!projectId) {
    return (
      <EmptyState
        icon={<Newspaper className="h-12 w-12" />}
        title="No Project Selected"
        description="Please select a project to view industry news and policy feeds"
      />
    );
  }
  
  const saveAlertSettings = () => {
    localStorage.setItem('rfp-selected-feeds', JSON.stringify(selectedFeedIds));
    localStorage.setItem('rfp-selected-keywords', JSON.stringify(selectedKeywords));
    setAlertSettingsOpen(false);
  };

  const showRelevanceScores = selectedKeywords.length > 0 && !rankingUnavailable;

  return (
    <div className="space-y-6">
      {/* RSS Industry News — filtered by active geographic zone */}
      {zoneFilteredFeedIds.length === 0 ? (
        <EmptyState
          icon={<Newspaper className="h-12 w-12" />}
          title="No feeds for this region"
          description="None of your selected RSS feeds match the current region. Open Alert Settings or switch region."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <Card className="p-4">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-yellow-600" />
                <h4 className="text-lg font-semibold">Industry News</h4>
                {isRanking && (
                  <Badge variant="primary" className="text-xs">
                    Ranking…
                  </Badge>
                )}
                {isFetchingRSS && rssItems.length > 0 && (
                  <Badge variant="primary" className="text-xs">
                    Updating feeds…
                  </Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant={newsSortMode === 'relevance' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setNewsSortMode('relevance')}
                  disabled={selectedKeywords.length === 0}
                  title={
                    selectedKeywords.length === 0
                      ? 'Add alert keywords to sort by relevance'
                      : undefined
                  }
                >
                  <ArrowUpDown className="h-4 w-4" />
                  Relevance
                </Button>
                <Button
                  variant={newsSortMode === 'date' ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setNewsSortMode('date')}
                >
                  Date
                </Button>
                <div className="relative" ref={alertSettingsRef}>
                  <Button
                    variant={alertSettingsOpen ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setAlertSettingsOpen((open) => !open)}
                    aria-expanded={alertSettingsOpen}
                    aria-haspopup="dialog"
                  >
                    <Settings className="h-4 w-4" />
                    Alert Settings
                  </Button>
                  {alertSettingsOpen && (
                    <div className="absolute right-0 top-full z-50 mt-2 max-h-[70vh] w-[min(28rem,calc(100vw-2rem))] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
                      <IndustryNewsAlertSettingsPanel
                        projectId={projectId}
                        selectedFeedIds={selectedFeedIds}
                        onSelectedFeedIdsChange={setSelectedFeedIds}
                        selectedKeywords={selectedKeywords}
                        onSelectedKeywordsChange={setSelectedKeywords}
                        similarityThreshold={similarityThreshold}
                        onSimilarityThresholdChange={setSimilarityThreshold}
                        onSave={saveAlertSettings}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
            <IndustryNewsSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onClear={clearSearch}
              resultCount={hasSearch ? filteredRssItems.length : undefined}
            />
            {!hasSearch && rankingUnavailable && (
              <p className="text-xs text-amber-600 mb-3">
                Relevance ranking unavailable — showing {filteredRssItems.length} articles sorted by date.
              </p>
            )}
            {!hasSearch && !rankingUnavailable && selectedKeywords.length > 0 && (
              <p className="text-xs text-gray-500 mb-3">
                {formatIndustryNewsThresholdSummary(
                  aboveThresholdCount,
                  filteredRssItems.length,
                  Math.round(similarityThreshold * 100)
                )}
              </p>
            )}
            {feedLoadProgress.loadingMessage && (
              <p className="text-xs text-blue-600 mb-3 flex items-center gap-1">
                <Clock className="h-3 w-3 animate-spin" />
                {feedLoadProgress.loadingMessage} ({rssItems.length} articles)
              </p>
            )}
            {loadingRSS && filteredRssItems.length === 0 ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : filteredRssItems.length === 0 && !isFetchingRSS ? (
              <EmptyState
                icon={<Newspaper className="h-8 w-8" />}
                title={hasSearch ? 'No Matching Articles' : 'No RSS Items'}
                description={
                  hasSearch
                    ? `No articles match "${searchQuery.trim()}" — try different keywords or clear the search`
                    : 'No news items found — lower the threshold or add keywords'
                }
              />
            ) : (
              <>
                <div className="space-y-3">
                  {newsPageItems.map((item) => (
                    <IndustryNewsArticleCard
                      key={item.id}
                      item={item}
                      relevanceScore={
                        showRelevanceScores && !item.relevanceScorePending
                          ? item.relevanceScore
                          : undefined
                      }
                      relevanceScorePending={
                        showRelevanceScores && item.relevanceScorePending === true
                      }
                      highlightQuery={hasSearch ? searchQuery : undefined}
                      titleClassName="font-semibold mb-1"
                      containerClassName="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      onView={(article) => {
                        setSelectedArticle(article);
                        setArticleModalOpen(true);
                      }}
                    />
                  ))}
                </div>
                <IndustryNewsPagination
                  page={newsPage}
                  totalItems={filteredRssItems.length}
                  onPageChange={setNewsPage}
                />
              </>
            )}
            <IndustryNewsArticleModal
              item={selectedArticle}
              isOpen={articleModalOpen}
              onClose={() => {
                setArticleModalOpen(false);
                setSelectedArticle(null);
              }}
            />
          </Card>
        </div>
      )}

      {showFederalRegisterTabs ? (
        <>
          <Card className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5 text-blue-600" />
                <h4 className="text-lg font-semibold">Federal Register</h4>
              </div>
              <div className="flex items-center gap-2">
                {federalAlerts.length > INDUSTRY_NEWS_PAGE_SIZE && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFederalPage(Math.max(1, federalPage - 1))}
                      disabled={federalPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-500">
                      Page {federalPage} of {federalTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFederalPage(Math.min(federalTotalPages, federalPage + 1))}
                      disabled={federalPage >= federalTotalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
            {loadingFederal ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : federalAlerts.length === 0 ? (
              <EmptyState
                icon={<FileText className="h-8 w-8" />}
                title="No Federal Register Alerts"
                description="No documents found matching your keywords"
              />
            ) : (
              <>
                <div className="space-y-3">
                  {federalPageItems.map((alert) => (
                    <div
                      key={alert.id}
                      className="rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 dark:text-black">
                          <div className="mb-1 flex items-center gap-2">
                            <Badge variant="primary" className="text-xs">
                              {alert.type}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {new Date(alert.publication_date).toLocaleDateString()}
                            </span>
                          </div>
                          <h5 className="mb-1 font-semibold">{alert.title}</h5>
                          {alert.summary && (
                            <p className="line-clamp-2 text-sm text-gray-600">{alert.summary}</p>
                          )}
                        </div>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => window.open(alert.html_url, '_blank')}
                        >
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {federalAlerts.length > INDUSTRY_NEWS_PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-between border-t pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFederalPage(Math.max(1, federalPage - 1))}
                      disabled={federalPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {federalPage} of {federalTotalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFederalPage(Math.min(federalTotalPages, federalPage + 1))
                      }
                      disabled={federalPage >= federalTotalPages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>

          <Card className="p-4">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-semibold">Executive Orders</h4>
              <div className="flex items-center gap-2">
                <select
                  value={eoFilters.year}
                  onChange={(event) => {
                    setEoFilters({ ...eoFilters, year: event.target.value });
                    setEoPage(1);
                  }}
                  className="rounded border px-2 py-1 text-sm dark:text-black"
                >
                  {eoYearOptions.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-3">
              {loadingEOs ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : filteredExecutiveOrders.length === 0 ? (
                <EmptyState
                  icon={<FileText className="h-8 w-8" />}
                  title="No Executive Orders"
                  description="No executive orders found matching your criteria"
                />
              ) : (
                <>
                  {filteredExecutiveOrders
                    .slice((eoPage - 1) * 5, eoPage * 5)
                    .map((eo) => (
                      <div
                        key={eo.id}
                        className="rounded-lg border bg-gray-50 p-4 transition-shadow hover:shadow-md"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 dark:text-black">
                            <div className="mb-2 flex items-center gap-2">
                              <Badge variant="primary" className="text-xs">
                                EO {eo.executive_order_number || eo.document_number}
                              </Badge>
                              <Badge variant="primary" className="text-xs">
                                {eo.president}
                              </Badge>
                              <span className="text-xs text-gray-500">
                                {new Date(eo.publication_date).toLocaleDateString()}
                              </span>
                            </div>
                            <h5 className="mb-2 line-clamp-2 font-semibold">{eo.title}</h5>
                            {eo.summary && (
                              <p className="mb-3 line-clamp-3 text-sm text-gray-600">
                                {eo.summary}
                              </p>
                            )}
                          </div>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => window.open(eo.html_url, '_blank')}
                          >
                            View
                          </Button>
                        </div>
                      </div>
                    ))}

                  {filteredExecutiveOrders.length > 5 && (
                    <div className="flex items-center justify-between border-t pt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEoPage(Math.max(1, eoPage - 1))}
                        disabled={eoPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-gray-600">
                        Page {eoPage} of {Math.ceil(filteredExecutiveOrders.length / 5)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setEoPage(
                            Math.min(
                              Math.ceil(filteredExecutiveOrders.length / 5),
                              eoPage + 1
                            )
                          )
                        }
                        disabled={eoPage >= Math.ceil(filteredExecutiveOrders.length / 5)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        </>
      ) : (
        <UsFederalRegisterRegionCta
          onSelectUnitedStates={() => setSelectedGeographicZone('usa')}
        />
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Newspaper className="h-8 w-8 text-blue-600" />
            <div>
              <div className="text-2xl font-bold">{filteredRssItems.length}</div>
              <div className="text-sm text-gray-600">
                {hasSearch ? 'Matching RSS Items' : 'RSS Items'}
              </div>
            </div>
          </div>
        </Card>
        
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <FileText className="h-8 w-8 text-purple-600" />
            <div>
              <div className="text-2xl font-bold">
                {showFederalRegisterTabs ? federalAlerts.length : '—'}
              </div>
              <div className="text-sm text-gray-600">Federal Alerts</div>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <Scale className="h-8 w-8 text-green-600" />
            <div>
              <div className="text-2xl font-bold">
                {showFederalRegisterTabs ? executiveOrders.length : '—'}
              </div>
              <div className="text-sm text-gray-600">Executive Orders</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

